import { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Row, Col, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import GoogleAutoComplete from 'react-google-autocomplete';
import PropTypes from 'prop-types';
import { updateCustomerAddress, updateCustomerRental, checkZipCode } from '../../../../../data/API';

const EditCustomerModal = ({ open, onClose, customerData, getUserData, extraWork }) => {
  const [form] = Form.useForm();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [pickupAddress, setPickupAddress] = useState(null);
  const [zipCodeError, setZipCodeError] = useState(null);
  const [deliveryAddressError, setDeliveryAddressError] = useState(null);
  const [pickupAddressError, setPickupAddressError] = useState(null);
  const [pickupZipCodeError, setPickupZipCodeError] = useState(null);

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  const getUserTypeFromToken = () => {
    try {
      const token = localStorage.getItem('token') || customerData?.token;
      if (!token) return 'customer';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.type;
    } catch (error) {
      return 'customer';
    }
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNum = value.replace(/[^\d]/g, '');
    const phoneLen = phoneNum.length;
    if (phoneLen < 4) return phoneNum;
    if (phoneLen < 7) {
      return `(${phoneNum.slice(0, 3)}) ${phoneNum.slice(3)}`;
    }
    return `(${phoneNum.slice(0, 3)}) ${phoneNum.slice(3, 6)}-${phoneNum.slice(6, 10)}`;
  };

  const validateMobileNumber = (e) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePrimary = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    form.setFieldsValue({ mobile_number: formattedPhone });
  };

  const handleSecondary = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    form.setFieldsValue({ secondary_number: formattedPhone });
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (!open || !customerData) return;

    const userType = getUserTypeFromToken();
    const isRental = userType === 'rental_customer';

    if (isRental) {
      form.setFieldsValue({
        name: customerData?.name,
        email: customerData?.email,
        mobile_number: customerData?.custormerData?.mobile
          ? formatPhoneNumber(customerData?.custormerData?.mobile)
          : '',
        secondary_number: customerData?.custormerData?.secondary_mobile
          ? formatPhoneNumber(customerData?.custormerData?.secondary_mobile)
          : '',
        // delivery_apt_number: customerData?.custormerData?.delivery_address?.apt_number || '',
        // pickup_apt_number: customerData?.custormerData?.pickup_address?.apt_number || '',
      });

      setDeliveryAddress(customerData?.custormerData?.delivery_address?.address || '');
      setPickupAddress(customerData?.custormerData?.pickup_address?.address || '');
    } else {
      form.setFieldsValue({
        name: customerData?.name,
        email: customerData?.email,
        mobile_number: customerData?.custormerData?.mobile
          ? formatPhoneNumber(customerData?.custormerData?.mobile)
          : '',
        secondary_number: customerData?.custormerData?.secondary_mobile
          ? formatPhoneNumber(customerData?.custormerData?.secondary_mobile)
          : '',
        apt_number: customerData?.custormerData?.apt_number || '',
        extra_work_id: customerData?.custormerData?.extra_work_id || ''
      });

      setDeliveryAddress(customerData?.custormerData?.address || '');
    }
  }, [open, customerData, form]);

  const handleCancel = () => {
    onClose();
    form.resetFields();
  };

  const setAddress = async (place) => {
    setDeliveryAddress(place?.formatted_address);

    let postal;
    place.address_components.forEach((address_element) => {
      if (address_element.types.includes('postal_code')) postal = address_element.long_name;
    });

    try {
      const zipCodeRes = await checkZipCode({ deliveryzipcode: postal || '781006' });
      if (zipCodeRes?.data?.success === true) {
        setZipCodeError(null);
      } else {
        setZipCodeError(zipCodeRes?.data?.message_2);
      }
    } catch (error) {
      console.error("Error checking zip code:", error);
    }
  };

  const setPickupAddressFunc = async (place) => {
    setPickupAddress(place?.formatted_address);

    let postal;
    place.address_components.forEach((address_element) => {
      if (address_element.types.includes('postal_code')) postal = address_element.long_name;
    });

    try {
      const zipCodeRes = await checkZipCode({ deliveryzipcode: postal || '781006' });
      if (zipCodeRes?.data?.success === true) {
        setPickupZipCodeError(null);
      } else {
        setPickupZipCodeError(zipCodeRes?.data?.message_2);
      }
    } catch (error) {
      console.error("Error checking pickup zip code:", error);
    }
  };

  const onAddresschange = (e, addressType = 'delivery') => {
    if (addressType === 'delivery') {
      if (deliveryAddress && e.target.value == 0) {
        setDeliveryAddress('');
        setDeliveryAddressError('Please Select Delivery Address');
      }
    } else if (addressType === 'pickup') {
      if (pickupAddress && e.target.value == 0) {
        setPickupAddress('');
        setPickupAddressError('Please Select Pickup Address');
      }
    }
  };

  const onSubmitCustomer = async () => {
    try {
      const formValues = await form.validateFields();
      setIsFormSubmitted(true);
      
      const userType = getUserTypeFromToken();
      const isRental = userType === 'rental_customer';

      if (zipCodeError || (isRental && pickupZipCodeError)) {
        setIsFormSubmitted(false);
        return;
      }
      
      let payload = {
        mobile: formValues?.mobile_number
          .replace('(', '')
          .replace(')', '')
          .replace(' ', '')
          .replace('-', ''),
      };
      
      if (isRental) {
        payload.delivery_address = deliveryAddress;
        payload.pickup_address = pickupAddress;
        
        // if (formValues.delivery_apt_number) {
        //   payload.delivery_apt_number = formValues.delivery_apt_number;
        // }
        // if (formValues.pickup_apt_number) {
        //   payload.pickup_apt_number = formValues.pickup_apt_number;
        // }
      } else {
        payload.address = deliveryAddress;
        
        if (formValues.apt_number) {
          payload.apt_number = formValues.apt_number;
        }
      }
      
      if (formValues.secondary_number) {
        payload.secondary_mobile = formValues?.secondary_number
          .replace('(', '')
          .replace(')', '')
          .replace(' ', '')
          .replace('-', '');
      }
      
      if (formValues.extra_work_id) {
        payload.extra_work_id = formValues.extra_work_id;
      }
      
      let response;
      if (userType === "customer") { 
        response = await updateCustomerAddress(payload);
      } else if (userType === "rental_customer") { 
        response = await updateCustomerRental(payload);
      }

      const isSuccess = userType === "customer" 
        ? response?.data?.success 
        : response?.data?.status === "success";
      
      if (isSuccess) {
        onClose();
        setIsFormSubmitted(false);
        message.success('Personal Details updated successfully...!');

        if (response?.data?.data) {
          getUserData(response?.data?.data);
        }
      } else {
        setIsFormSubmitted(false);
        message.error('Unable to update customer, Please try again...!');
      }      
    } catch (err) {
      console.error('Error in onSubmitCustomer:', err);
      setIsFormSubmitted(false);
      
      if (!err.errorFields) {
        message.error('Unable to update customer, Please try again...!');
      }
    }
  };

  return (
    <Modal
      footer={false}
      centered
      title={
        <div className="d-flex justify-content-end align-items-center">
          <CloseOutlined className="cursor-pointer" onClick={() => handleCancel()} />
        </div>
      }
      closable={false}
      open={open}
      width={1200}>
      <Form form={form} layout="vertical" autoComplete="off">
        <Spin spinning={isFormSubmitted} tip={'processing....'}>
          <Row className="form-layout" gutter={16}>
            {/* First Row - Name and Email */}
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the name'
                  }
                ]}>
                <Input size="large" placeholder="input placeholder" disabled={true} />
              </Form.Item>
            </Col>

            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  {
                    required: true,
                    message: 'Please enter the email'
                  },
                  {
                    type: 'email',
                    message: 'The input is not valid E-mail!'
                  }
                ]}>
                <Input size="large" placeholder="input placeholder" disabled={true} />
              </Form.Item>
            </Col>

            {/* Second Row - Phone Numbers */}
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                name="mobile_number"
                label="Mobile number"
                validateTrigger="onBlur"
                rules={[
                  { required: true, message: 'Please input the mobile number', min: 14 }
                ]}>
                <Input
                  size="large"
                  placeholder="input placeholder"
                  minLength={14}
                  maxLength={14}
                  onKeyPress={(event) => {
                    validateMobileNumber(event);
                  }}
                  onChange={(e) => handlePrimary(e)}
                />
              </Form.Item>
            </Col>

            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item
                name="secondary_number"
                label="Secondary phone number"
                validateTrigger="onBlur"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (
                        getFieldValue('secondary_number') &&
                        getFieldValue('secondary_number').length != 14
                      ) {
                        return Promise.reject('Please enter valid number');
                      } else {
                        return Promise.resolve();
                      }
                    }
                  })
                ]}>
                <Input
                  size="large"
                  placeholder="input placeholder"
                  minLength={14}
                  maxLength={14}
                  onKeyPress={(event) => {
                    validateMobileNumber(event);
                  }}
                  onChange={(e) => handleSecondary(e)}
                />
              </Form.Item>
            </Col>

            {/* Submit Button Row */}
            <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
              <div className="d-flex justify-content-between align-items-center">
                {getUserTypeFromToken() === 'rental_customer' ? (
                  <p className="mb-0" style={{ fontWeight: 500, color: '#1890ff' }}>
                    Note: You can manage delivery/pickup dates and address in Your Orders section
                  </p>
                ) : (
                  <div></div>
                )}
                <Form.Item>
                  <Button
                    size="large"
                    type="primary"
                    className="signup-register-btn"
                    htmlType="submit"
                    onClick={() => onSubmitCustomer()}>
                    {isFormSubmitted ? 'Processing..' : 'Save details'}
                  </Button>
                </Form.Item>
              </div>
            </Col>
          </Row>
        </Spin>
      </Form>
    </Modal>
  );
};

EditCustomerModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerData: PropTypes.object,
  getUserData: PropTypes.func,
  extraWork: PropTypes.array
};

export default EditCustomerModal;